import { useMemo, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext.jsx";
import { useBoard } from "../board/BoardContext.jsx";
import { AckBar } from "../components/AckBar.jsx";
import { SummaryRail } from "../components/SummaryRail.jsx";
import { BoardFilter, matchesFilter } from "../components/BoardFilter.jsx";
import { PlaygroundTile } from "../components/PlaygroundTile.jsx";
import { BoardPager } from "../components/BoardPager.jsx";

/* One screenful. Divides by two, three and four, so the last row of the
   grid is full at every column count the layout uses. */
const PER_PAGE = 12;

/* The one screen: the day's acknowledgements, the portfolio in five
   figures, then the ten playgrounds. The drawer for a single playground is
   a nested route rendered over this, so the board stays behind it. */
export function DashboardPage() {
  const { board, loading, error, create } = useBoard();
  const { isAdmin } = useAuth();
  const [q, setQ] = useState("");
  const [states, setStates] = useState([]);
  const [adding, setAdding] = useState(false);
  /* Which page is wanted. What is shown is `page` clamped to what exists —
     see `current` below — so a board that shrinks under you, by a filter or
     by someone else's delete, cannot leave you on a page that is gone. */
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  /* Health is derived per tile, so recompute the visible set only when the
     board or the filter actually moves. The board is null until the first
     read lands, and a hook cannot wait behind the early returns below, so
     the empty case is handled here rather than there. */
  const shown = useMemo(
    () => (board ?? []).filter((pg) => matchesFilter(pg, q, states)),
    [board, q, states],
  );

  const pages = Math.max(1, Math.ceil(shown.length / PER_PAGE));
  const current = Math.min(page, pages);
  const start = (current - 1) * PER_PAGE;
  const visible = shown.slice(start, start + PER_PAGE);

  /* Changing the page moves the grid out from under wherever you were
     reading, so go back to the top of it. Instant rather than smooth: the
     stylesheet drops its transitions under prefers-reduced-motion, and a
     scroll animation nobody asked for would be the one thing left moving. */
  function goTo(n) {
    setPage(Math.min(Math.max(n, 1), pages));
    window.scrollTo({ top: 0 });
  }

  /* Narrowing the board renumbers the pages under it, so any change to the
     filter starts again at the first one. */
  function search(next) {
    setQ(next);
    setPage(1);
  }

  function toggle(key) {
    setStates((cur) =>
      cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key],
    );
    setPage(1);
  }

  function clear() {
    setQ("");
    setStates([]);
    setPage(1);
  }

  /* One action behind two controls — the tile at the end of the grid and
     the button in the filter bar — so neither owns the busy flag.
   *
   * A new playground is empty and unnamed, so it answers to no filter: it
   * would be created and then vanish from a narrowed board. Drop the
   * filter, so the thing that was just made is on screen behind the
   * drawer that opens over it. */
  async function addPlayground() {
    setAdding(true);
    try {
      const pg = await create();
      clear();
      /* A new playground is appended, so with the filter dropped it is the
         last tile of the last page — which is not the page you were on.
         Computed from the length this call just produced rather than from
         `board`, which is a render behind. */
      setPage(Math.ceil((board.length + 1) / PER_PAGE));
      navigate("/playground/" + pg.id);
    } catch {
      /* BoardContext has already said what went wrong. */
    } finally {
      setAdding(false);
    }
  }

  if (loading) {
    return <p className="empty">Reading the board…</p>;
  }

  if (error) {
    return (
      <div className="notice">
        <b>The board could not be read.</b> {error}
      </div>
    );
  }

  return (
    <>
      <AckBar />
      {/* The rail stays the whole portfolio even while the grid is
          narrowed — it is the summary of the board, not of the view. */}
      <SummaryRail board={board} />
      {/* The bar is here for any board that has something on it. It used to
          appear only from seven playgrounds up, on the reasoning that a
          short board is one glance — but that also took away the search,
          the three state chips and the bar's own Add button for every board
          below that, which is most of them. An empty board is the one case
          with nothing to search, and the add tile below speaks for it. */}
      {board.length > 0 && (
        <BoardFilter
          q={q}
          states={states}
          onQuery={search}
          onToggle={toggle}
          onClear={clear}
          shown={shown.length}
          total={board.length}
          onAdd={addPlayground}
          adding={adding}
        />
      )}
      {/* An empty board is still the board. It used to return a bare
          sentence instead of rendering any of this, which took the
          acknowledgements, the rail, the add tile and — because the drawer
          is a nested route — /playground/:id with it: the tab went blank
          apart from the one line, and there was no way to make the first
          playground from the screen that said there were none. */}
      {!board.length ? (
        <>
          <div className="notice">
            <b>No playgrounds yet.</b> Add the first one below.{" "}
            {isAdmin
              ? "Data can also reset the board to the ten seed scaffolds, or import a JSON export."
              : "An administrator can also seed the ten scaffolds, or import a JSON export."}
          </div>
          <section className="grid" aria-label="Playgrounds">
            <AddTile onAdd={addPlayground} busy={adding} />
          </section>
        </>
      ) : shown.length === 0 ? (
        <div className="notice">
          <b>Nothing matches.</b> No playground answers to that name, owner or
          state.{" "}
          <button className="btn btn-sm" type="button" onClick={clear}>
            Clear the filter
          </button>
        </div>
      ) : (
        <>
          <section className="grid" aria-label="Playgrounds">
            {visible.map((pg) => (
              <PlaygroundTile key={pg.id} pg={pg} />
            ))}
            {/* The tile is the cell after the last playground, so it belongs
                on the last page. On any earlier one it would sit mid-board
                with tiles after it, and the filter bar's copy of the action
                is in reach from every page anyway. */}
            {current === pages && (
              <AddTile onAdd={addPlayground} busy={adding} />
            )}
          </section>
          {pages > 1 && (
            <BoardPager
              page={current}
              pages={pages}
              from={start + 1}
              to={start + visible.length}
              total={shown.length}
              onPage={goTo}
            />
          )}
        </>
      )}
      <Outlet />
    </>
  );
}

/* The last cell of the grid rather than a button above it: the board is
   the ten playgrounds, so the way to get an eleventh belongs among them.
   Past a screenful that stops being reachable, which is what the copy of
   this action in the filter bar is for — the tile stays because it is
   still the right gesture when the whole board is in view.
 *
 * A new playground is created empty and its drawer opened straight away —
 * everything on this board is named in place, and asking for a name in a
 * dialog first would be the only modal in the application. */
function AddTile({ onAdd, busy }) {
  return (
    <button className="tile addtile" type="button" onClick={onAdd} disabled={busy}>
      <span className="plus" aria-hidden="true">+</span>
      <span className="addlabel">{busy ? "Adding…" : "Add playground"}</span>
      <span className="addhint">Named and filled in after it opens</span>
    </button>
  );
}
